/**
 * Notification Channel Service Types
 *
 * Type definitions for notification channel API operations.
 */

import type { NotificationChannel } from '../../components/dashboard/NotificationChannels';

/**
 * Error thrown by notification channel API operations
 */
export class NotificationChannelApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly channelId?: number
  ) {
    super(message);
    this.name = 'NotificationChannelApiError';
  }
}

/**
 * Response when fetching channels
 */
export interface FetchChannelsResult {
  channels: NotificationChannel[];
}

/**
 * Response when creating a channel
 */
export interface CreateChannelResult {
  channel: NotificationChannel;
}

/**
 * Parameters for creating an email channel
 */
export interface CreateEmailChannelParams {
  name: string;
  email: string;
  emailType?: 'notification' | 'digest';
}

/**
 * Parameters for creating a Slack channel
 */
export interface CreateSlackChannelParams {
  name: string;
  webhookUrl: string;
}

/**
 * Parameters for creating a webhook channel
 */
export interface CreateWebhookChannelParams {
  name: string;
  url: string;
  headers?: Record<string, string>;
}

/**
 * Parameters for updating a channel
 */
export interface UpdateChannelParams {
  channelId: number;
  enabled?: boolean;
}

/**
 * Parameters for verifying a channel
 */
export interface VerifyChannelParams {
  channelId: number;
  code: string;
}

// Re-export NotificationChannel type for convenience
export type { NotificationChannel };
